import React, { useEffect, useState } from "react";
import api from "../../utils/api";

const asArray = (value) => (Array.isArray(value) ? value : []);

const formatTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/London" });
};

const formatClockLabel = (value) => {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (/^\d{2}:\d{2}$/.test(text)) return text;
  if (/^\d{2}:\d{2}:\d{2}$/.test(text)) return text.slice(0, 5);
  return text;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" });
};

const formatDuration = (mins) => {
  const n = Number(mins);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  if (h <= 0) return `${m} mins`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const formatRoadDeltaMins = (secs) => {
  const n = Number(secs);
  if (!Number.isFinite(n)) return "vs usual N/A";
  const mins = Math.round(n / 60);
  if (mins === 0) return "on usual time";
  return `${mins > 0 ? "+" : ""}${mins}m vs usual`;
};

const formatSignedMinutes = (secs) => {
  const n = Number(secs);
  if (!Number.isFinite(n)) return "—";
  const mins = Math.round(n / 60);
  if (mins === 0) return "0m";
  return `${mins > 0 ? "+" : ""}${mins}m`;
};

const hasMaterialDelay = (secs) => Number(secs || 0) >= 120;

const formatMiles = (meters) => {
  const n = Number(meters);
  if (!Number.isFinite(n)) return "—";
  return `${(n / 1609.344).toFixed(1)} mi`;
};

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `£${n.toFixed(2)}`;
};

const opsApiRequest = async (url) => {
  const res = await api.request({ url, method: "GET" });
  return res?.data;
};

const opsStatusColor = (level) => {
  const key = String(level || "unknown").toLowerCase();
  if (key === "green") return { bg: "#ecfdf5", border: "#86efac", text: "#166534" };
  if (key === "light_green") return { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534" };
  if (key === "amber") return { bg: "#fffbeb", border: "#fcd34d", text: "#b45309" };
  if (key === "red" || key === "critical_red") return { bg: "#fef2f2", border: "#fca5a5", text: "#b91c1c" };
  return { bg: "#f8fafc", border: "#cbd5e1", text: "#475569" };
};

const opsBadge = (level) => {
  const palette = opsStatusColor(level);
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 10px",
    borderRadius: 999,
    border: `1px solid ${palette.border}`,
    background: palette.bg,
    color: palette.text,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.35,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };
};

const cardTheme = {
  road: {
    border: "#93c5fd",
    bg: "linear-gradient(180deg, #eaf4ff 0%, #f8fbff 28%, #ffffff 100%)",
    accent: "#2563eb",
  },
  rail: {
    border: "#c4b5fd",
    bg: "linear-gradient(180deg, #f2edff 0%, #fbf9ff 28%, #ffffff 100%)",
    accent: "#4338ca",
  },
  park: {
    border: "#fca5a5",
    bg: "linear-gradient(180deg, #fff4e6 0%, #fffaf3 28%, #ffffff 100%)",
    accent: "#d97706",
  },
};

const opsCardStyle = (theme) => ({
  border: `1.5px solid ${theme.border}`,
  borderRadius: 18,
  background: theme.bg,
  padding: 16,
  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.07)",
});

const opsMiniLabel = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.35,
  textTransform: "uppercase",
  color: "#64748b",
};

const formatRailIssueLine = (today = {}) => {
  const delayed = Number(today?.delayed_runs_today || 0);
  const cancelled = Number(today?.cancelled_runs_today || 0);
  return `Today: delayed ${delayed} • cancelled ${cancelled}${today?.last_issue_at ? ` • last issue ${formatTime(today.last_issue_at)}` : ""}`;
};

const formatRailNextLine = (board = {}) => {
  const nextService =
    asArray(board?.departures)[0] ||
    asArray(board?.arrivals)[0] ||
    asArray(board?.relevant_services)[0] ||
    null;
  if (!nextService) return "No live board service";
  const timeText =
    nextService?.std_local ||
    nextService?.expected_time_local ||
    nextService?.departure_scheduled_local ||
    nextService?.arrival_scheduled_local ||
    "Live";
  const context = String(nextService?.context_summary || "").trim();
  const statusText = String(nextService?.status_text || "").trim();
  const expected = String(nextService?.expected_time_local || nextService?.departure_expected_local || nextService?.arrival_expected_local || "").trim();
  const parts = [timeText];
  if (context) parts.push(context);
  if (statusText && !["on time", timeText.toLowerCase()].includes(statusText.toLowerCase())) {
    parts.push(/^\d{2}:\d{2}$/.test(statusText) ? `exp ${statusText}` : statusText);
  } else if (expected && /^\d{2}:\d{2}$/.test(expected) && expected !== timeText) {
    parts.push(`exp ${expected}`);
  } else if (statusText && statusText.toLowerCase() === "on time") {
    parts.push("On time");
  }
  return parts.join(" • ");
};

const buildRoadGuidance = (corridor, worstActiveSegment) => {
  const deltaSec = Number(corridor?.route_delta_sec || 0);
  const label = worstActiveSegment?.display_label || null;
  if (deltaSec >= 900) return label ? `Guidance: allow at least 15 extra mins, mainly around ${label}.` : "Guidance: allow at least 15 extra mins on this route.";
  if (deltaSec >= 420) return label ? `Guidance: allow 5-10 extra mins, mainly around ${label}.` : "Guidance: allow 5-10 extra mins on this route.";
  if (deltaSec >= 120) return label ? `Guidance: small slowdown showing around ${label}.` : "Guidance: a small slowdown is showing on this route.";
  return "Guidance: route broadly normal right now.";
};

export default function LegolandLiveSection() {
  const [snapshot, setSnapshot] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setSnapshot((prev) => ({ ...prev, loading: true, error: "" }));
        const data = await opsApiRequest("/api/open/legoland/live-summary");
        if (cancelled) return;
        setSnapshot({ loading: false, error: "", data });
      } catch (err) {
        if (cancelled) return;
        const message = err?.response?.data?.error || err?.message || "Unable to load LEGOLAND live status.";
        setSnapshot({ loading: false, error: message, data: null });
      }
    };

    load();
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const road = snapshot.data?.road || { corridors: [], details: {}, today: {}, historic: {}, historic_route: {} };
  const rail = snapshot.data?.rail || { boards: [], today: {} };
  const park = snapshot.data?.park || {};

  const roadRows = asArray(road.corridors).map((corridor) => {
    const detail = road.details?.[corridor.corridor_key] || {};
    const segments = asArray(detail?.segments);
    const abnormalSegments = segments.filter((segment) => ["amber", "red", "critical_red"].includes(String(segment?.status_label || "").toLowerCase()));
    const isToLegoland = String(corridor.destination_label || "").toUpperCase() === "LEGOLAND";
    const edgeSegment = isToLegoland ? segments[segments.length - 1] || null : segments[0] || null;
    const today = road.today?.[corridor.corridor_key] || null;
    const historic = road.historic?.[corridor.corridor_key] || null;
    const historicRoute = road.historic_route?.[corridor.corridor_key] || null;
    return { corridor, segments, abnormalSegments, isToLegoland, edgeSegment, today, historic, historicRoute };
  });

  const inboundRoadRows = roadRows.filter((row) => row.isToLegoland);
  const outboundRoadRows = roadRows.filter((row) => !row.isToLegoland);

  const roadOverallLevel = roadRows.some((row) => ["red", "critical_red"].includes(String(row.corridor?.status_label || "").toLowerCase()))
    ? "red"
    : roadRows.some((row) => String(row.corridor?.status_label || "").toLowerCase() === "amber")
    ? "amber"
    : roadRows.some((row) => String(row.corridor?.status_label || "").toLowerCase() === "light_green")
    ? "light_green"
    : "green";

  const railRows = asArray(rail.boards).map((row) => ({
    key: row?.target?.target_key || row?.target?.display_name,
    label: row?.target?.display_name || row?.target?.target_key || "Station",
    status: row?.board?.status_label || row?.target?.effective_status_label || "unknown",
    summary: row?.board?.reason_text || row?.reason_text || "No live board service",
    next: formatRailNextLine(row),
    today: rail.today?.[row?.target?.target_key] || null,
  }));

  const railOverallLevel = railRows.some((row) => ["red", "critical_red"].includes(String(row.status || "").toLowerCase()))
    ? "red"
    : railRows.some((row) => String(row.status || "").toLowerCase() === "amber")
    ? "amber"
    : railRows.some((row) => String(row.status || "").toLowerCase() === "light_green")
    ? "light_green"
    : "green";

  const parkDay = park?.day || null;
  const parkStatus = parkDay?.is_open ? "green" : "red";
  const weather = park?.weather || null;
  const parking = park?.parking || null;
  const inboundTopStatus = inboundRoadRows[0]?.corridor?.status_label || "unknown";
  const outboundTopStatus = outboundRoadRows[0]?.corridor?.status_label || "unknown";
  const topRailStatus = railRows[0]?.status || railOverallLevel;

  const bestHistoricForGroup = (rows) =>
    rows
      .map((row) => ({ ...row, historic: row.historic || null }))
      .filter((row) => row.historic)
      .sort((a, b) => Number(b.historic?.delta_sec || 0) - Number(a.historic?.delta_sec || 0))[0] || null;

  const bestHistoricRouteForGroup = (rows) =>
    rows
      .map((row) => ({ ...row, historicRoute: row.historicRoute || null }))
      .filter((row) => row.historicRoute)
      .sort((a, b) => Number(b.historicRoute?.route_delta_sec || 0) - Number(a.historicRoute?.route_delta_sec || 0))[0] || null;

  const inboundHistoric = bestHistoricForGroup(inboundRoadRows);
  const outboundHistoric = bestHistoricForGroup(outboundRoadRows);
  const inboundHistoricRoute = bestHistoricRouteForGroup(inboundRoadRows);
  const outboundHistoricRoute = bestHistoricRouteForGroup(outboundRoadRows);

  const renderRoadGroup = (title, subtitle, rows, historicWinner, historicRouteWinner) => (
    <div style={{ display: "grid", gap: 10 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{title}</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{subtitle}</div>
      </div>
      {!rows.length ? (
        <div style={{ fontSize: 12, color: "#64748b" }}>No live corridor in this direction yet.</div>
      ) : null}
      {rows.map(({ corridor, segments, abnormalSegments, edgeSegment, today, historic, isToLegoland }) => (
        <details key={corridor.corridor_key} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px" }} open={false}>
          <summary style={{ cursor: "pointer", listStyle: "none" }}>
            {(() => {
              const worstActiveSegment = abnormalSegments
                .slice()
                .sort((a, b) => Number(b.delta_sec || 0) - Number(a.delta_sec || 0))[0] || null;
              const headlineSegment = worstActiveSegment || edgeSegment;
              const headlinePrefix = worstActiveSegment
                ? "Main delay now"
                : isToLegoland
                ? "Park approach now"
                : "Park exit now";
              const guidance = buildRoadGuidance(corridor, worstActiveSegment);
              return (
                <>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>{corridor.display_name || corridor.corridor_key}</div>
              <span style={opsBadge(corridor.status_label)}>{String(corridor.status_label || "unknown").toUpperCase()}</span>
            </div>
            <div style={{ marginTop: 6, fontSize: 13, color: "#334155" }}>
              {`${formatDuration(Number(corridor.route_duration_live_sec || 0) / 60)} • ${formatMiles(corridor.route_distance_m)} • ${formatRoadDeltaMins(corridor.route_delta_sec)}`}
            </div>
            {headlineSegment ? (
              <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                {`${headlinePrefix}: ${headlineSegment.display_label} • ${String(headlineSegment.status_label || "unknown").toUpperCase()} • ${formatDuration(Number(headlineSegment.duration_live_sec || 0) / 60)} • ${formatSignedMinutes(headlineSegment.delta_sec)}`}
              </div>
            ) : null}
            <div style={{ marginTop: 6, fontSize: 12, color: "#475569" }}>{guidance}</div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#2563eb", fontWeight: 700 }}>Tap to open full route sections</div>
                </>
              );
            })()}
          </summary>
          <div style={{ marginTop: 10, display: "grid", gap: 6, fontSize: 13, color: "#475569" }}>
            {abnormalSegments.length ? (
              abnormalSegments.map((segment) => (
                <div key={segment.segment_key} style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <span>{segment.display_label}</span>
                  <span>{`${formatDuration(Number(segment.duration_live_sec || 0) / 60)} • ${formatMiles(segment.distance_m)} • ${formatRoadDeltaMins(segment.delta_sec)}`}</span>
                </div>
              ))
            ) : (
              <div>Route broadly normal. Open the full route sections below if you want every step broken out.</div>
            )}
            {today?.worst_segment_key ? (
              <div style={{ fontSize: 12, color: "#64748b" }}>
                {`Today worst over-baseline: ${today.worst_segment_label} • ${formatRoadDeltaMins(today.worst_segment_delta_sec)} • ${formatTime(today.worst_segment_observed_at_utc)}`}
              </div>
            ) : null}
            {historic && hasMaterialDelay(historic.delta_sec) ? (
              <div style={{ fontSize: 12, color: "#64748b" }}>
                {`Historic busiest edge section: ${historic.display_label} • ${formatSignedMinutes(historic.delta_sec)} • ${formatDate(historic.observed_at_utc)} ${formatTime(historic.observed_at_utc)}`}
              </div>
            ) : null}
            <details style={{ marginTop: 6 }} open={abnormalSegments.length > 0}>
              <summary style={{ cursor: "pointer", color: "#2563eb", fontWeight: 700 }}>Show full route sections</summary>
              <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
                {segments.map((segment) => (
                  <div key={segment.segment_key} style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <span>{`${segment.display_label} • ${String(segment.status_label || "unknown").toUpperCase()}`}</span>
                    <span>{`${formatDuration(Number(segment.duration_live_sec || 0) / 60)} • ${formatMiles(segment.distance_m)} • ${formatSignedMinutes(segment.delta_sec)}`}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        </details>
      ))}
      {historicWinner?.historic && hasMaterialDelay(historicWinner.historic.delta_sec) ? (
        <div style={{ fontSize: 12, color: "#64748b", padding: "2px 2px 0" }}>
          {`Historic busiest ${title.toLowerCase()}: ${historicWinner.historic.display_label} on ${formatDate(historicWinner.historic.observed_at_utc)} ${formatTime(historicWinner.historic.observed_at_utc)} • ${formatDuration(Number(historicWinner.historic.duration_live_sec || 0) / 60)} • ${formatSignedMinutes(historicWinner.historic.delta_sec)}`}
        </div>
      ) : null}
      {historicRouteWinner?.historicRoute && hasMaterialDelay(historicRouteWinner.historicRoute.route_delta_sec) ? (
        <div style={{ fontSize: 12, color: "#64748b", padding: "0 2px 0" }}>
          {`Recent busiest period on this side: ${formatDate(historicRouteWinner.historicRoute.observed_at_utc)} ${formatTime(historicRouteWinner.historicRoute.observed_at_utc)} • ${historicRouteWinner.corridor.display_name} • ${formatDuration(Number(historicRouteWinner.historicRoute.route_duration_live_sec || 0) / 60)} • ${formatRoadDeltaMins(historicRouteWinner.historicRoute.route_delta_sec)}`}
        </div>
      ) : null}
    </div>
  );

  return (
    <section style={{ marginTop: 18, padding: "18px 0 0" }}>
      <details style={{ border: "1px solid #dbe7f2", borderRadius: 18, background: "#fff", padding: 0 }} open={false}>
        <summary style={{ cursor: "pointer", listStyle: "none", padding: "16px 18px" }}>
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.35, textTransform: "uppercase", color: "#2563eb" }}>
                Live LEGOLAND Status
              </div>
              <h2 style={{ margin: "6px 0 4px", fontSize: 24, lineHeight: 1.15, color: "#0f172a" }}>
                Legoland Live
              </h2>
              <div style={{ fontSize: 14, color: "#475569", maxWidth: 920 }}>
                Road, rail, tickets, parking and weather in one live view.
              </div>
              </div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  borderRadius: 999,
                  border: "1px solid #93c5fd",
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  fontSize: 13,
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                }}
              >
                <span>Tap to expand</span>
                <span aria-hidden="true">▾</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span style={opsBadge(inboundTopStatus)}>{`Road AM ${String(inboundTopStatus || "unknown").toUpperCase()}`}</span>
              <span style={opsBadge(outboundTopStatus)}>{`Road PM ${String(outboundTopStatus || "unknown").toUpperCase()}`}</span>
              <span style={opsBadge(topRailStatus)}>{`Rail ${String(topRailStatus || "unknown").toUpperCase()}`}</span>
              <span style={opsBadge(parkStatus)}>{`Park ${parkDay?.is_open ? "OPEN" : "CLOSED"}`}</span>
            </div>
          </div>
        </summary>

        <div style={{ padding: "0 18px 18px", display: "grid", gap: 10 }}>
          <div style={{ fontSize: 14, color: "#475569", maxWidth: 920 }}>
            This brings together the live road corridors, Slough-side rail monitoring, today&apos;s LEGOLAND pricing snapshot, parking pricing, and current weather in one compact view.
          </div>

          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
            <div style={opsCardStyle(cardTheme.road)}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ ...opsMiniLabel, color: cardTheme.road.accent }}>Road to LEGOLAND</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Peak road corridors</div>
              </div>
              <span style={opsBadge(roadOverallLevel)}>{snapshot.loading ? "Loading" : "Live"}</span>
            </div>
            {snapshot.error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{snapshot.error}</div> : null}
            {!snapshot.error && (
              <div style={{ display: "grid", gap: 14 }}>
                {renderRoadGroup(
                  "To LEGOLAND (morning)",
                  "Routes that matter most for inbound family traffic and opening-time pressure.",
                  inboundRoadRows,
                  inboundHistoric,
                  inboundHistoricRoute
                )}
                {renderRoadGroup(
                  "From LEGOLAND (evening)",
                  "Routes that matter most when the park empties and queues push back onto the road network.",
                  outboundRoadRows,
                  outboundHistoric,
                  outboundHistoricRoute
                )}
              </div>
            )}
            {!snapshot.error && !roadRows.length && <div style={{ color: "#64748b", fontSize: 13 }}>No LEGOLAND road corridor status available yet.</div>}
          </div>

            <div style={opsCardStyle(cardTheme.rail)}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ ...opsMiniLabel, color: cardTheme.rail.accent }}>Rail to LEGOLAND</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Slough side boards</div>
              </div>
              <span style={opsBadge(railOverallLevel)}>{snapshot.loading ? "Loading" : "Live"}</span>
            </div>
            {snapshot.error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{snapshot.error}</div> : null}
            {!snapshot.error && railRows.map((row) => (
              <div key={row.key} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{row.label}</div>
                  <span style={opsBadge(row.status)}>{String(row.status || "unknown").toUpperCase()}</span>
                </div>
                <div style={{ marginTop: 6, color: "#334155", fontSize: 13 }}>{row.summary}</div>
                <div style={{ marginTop: 6, color: "#64748b", fontSize: 13 }}>{row.next}</div>
                <div style={{ marginTop: 8, color: "#64748b", fontSize: 12 }}>{formatRailIssueLine(row.today)}</div>
              </div>
            ))}
            {!snapshot.error && !railRows.length && <div style={{ color: "#64748b", fontSize: 13 }}>No Slough / Windsor rail board status available yet.</div>}
          </div>

            <div style={opsCardStyle(cardTheme.park)}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ ...opsMiniLabel, color: cardTheme.park.accent }}>LEGOLAND today</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Tickets, parking, weather</div>
              </div>
              <span style={opsBadge(parkStatus)}>{snapshot.loading ? "Loading" : parkDay?.is_open ? "Open" : "Closed"}</span>
            </div>
            {snapshot.error ? <div style={{ color: "#b91c1c", fontSize: 13 }}>{snapshot.error}</div> : null}
            {!snapshot.error && (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Today&apos;s park status</div>
                  <div style={{ marginTop: 6, color: "#334155", fontSize: 13 }}>
                    {parkDay
                      ? parkDay.is_open
                        ? `Open ${parkDay.open_time && parkDay.close_time ? `${formatClockLabel(parkDay.open_time)}-${formatClockLabel(parkDay.close_time)}` : "today"}`
                        : "Closed today"
                      : "No current park day snapshot yet."}
                  </div>
                  {parkDay?.price_from_gbp != null ? (
                    <div style={{ marginTop: 6, color: "#64748b", fontSize: 13 }}>
                      {`From ${money(parkDay.price_from_gbp)} per ticket${parkDay.price_age_days != null ? ` • checked ${parkDay.price_age_days === 0 ? "today" : `${parkDay.price_age_days}d ago`}` : ""}`}
                    </div>
                  ) : null}
                </div>

                <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Parking today</div>
                  <div style={{ marginTop: 6, color: "#334155", fontSize: 13 }}>
                    {parking ? `Standard ${money(parking.standard_gbp)} • Priority ${money(parking.priority_gbp)} • Disabled ${money(parking.disabled_gbp)}` : "Parking price summary unavailable"}
                  </div>
                  <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>
                    {parking ? `${String(parking.season || "standard")} pricing season` : "LEGOLAND parking prices"}
                  </div>
                </div>

                <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 12px" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Current weather</div>
                  <div style={{ marginTop: 6, color: "#334155", fontSize: 13 }}>
                    {weather ? `${weather.weather_text} • ${weather.temp_c != null ? `${weather.temp_c}C` : "—"} • wind ${weather.wind_mps != null ? `${weather.wind_mps}m/s` : "—"}` : "No weather snapshot available"}
                  </div>
                  <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>
                    {weather?.observed_at ? `Observed ${formatTime(weather.observed_at)} • ${formatDate(weather.observed_at)}` : "Live park weather"}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </details>
    </section>
  );
}
